import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spin, message as antdMessage } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  SoundFilled,
  SoundOutlined,
  SwapOutlined
} from '@ant-design/icons';
import CustomModal from '@/common/CustomModal';
import QrCamera from './QrCamera';
import { playSuccessFeedback, playErrorFeedback } from './feedback';
import styles from './QrScan.module.less';

const SUCCESS_HOLD_MS = 600;
const ERROR_HOLD_MS = 1500;

/**
 * Single-shot QR scan modal — pretty camera UI for screens that take ONE scan
 * at a time (manual dispense container, weighing-balance equipment QR, etc.).
 *
 * The modal OWNS its close timing after a decode so it can show the
 * green/red confirmation feedback before unmounting. The caller is still the
 * source of truth for opening (visible=true) and for the visibility prop in
 * general — when the caller flips visible=false, the modal also closes.
 *
 * Props:
 *   visible  bool
 *   onCancel () => void                              — called when user clicks Cancel/X OR
 *                                                       when the modal auto-closes after a scan
 *   title    string
 *   onScan   (decodedText: string) =>
 *                  { ok?: boolean, message?: string } | Promise<...>
 *            Caller validates the scan and returns the result. Returning
 *            undefined or null is treated as success (used by handlers that
 *            never reject, e.g. balance scan). When { ok: false, message }
 *            is returned, the modal also fires antd.message.error(message)
 *            so the background screen sees the same toast it used to see.
 *   hint     string                                  — helper text under camera
 *
 * Lifecycle of a single scan:
 *   1. Camera decodes → handleDecode fires (debounced/dedup'd by QrCamera).
 *   2. inFlightRef set → camera UI shows a 'Validating…' overlay.
 *   3. onScan(decodedText) is awaited.
 *   4. If component unmounted / parent flipped visible=false meanwhile → bail
 *      silently (caller's state mutations still happen, this is just UI).
 *   5. Success: green flash + success chime + 'X confirmed' strip → 600ms → onCancel().
 *      Failure: red flash + error buzz + error strip + antdMessage.error(msg) → 1500ms → onCancel().
 *   6. Caller's onCancel handler flips visible=false → modal resets via useEffect.
 *
 * Edge cases handled:
 *   - Rapid duplicate decodes: QrCamera debounces; we also guard with inFlightRef.
 *   - Caller's onScan throws: treated as failure with a generic message.
 *   - User cancels mid-await: we detect via visibleRef and skip the auto-close path.
 *   - Camera permission denied / no device: in-modal error state, Cancel still works.
 *   - User toggles mute or switches camera mid-scan: independent state, no interference.
 *   - Modal closed and re-opened: full state reset on every visible→false transition.
 */
const QrScanSingleShotModal = ({
  visible,
  onCancel,
  title = 'Scan QR Code',
  onScan,
  hint
}) => {
  const [facingMode, setFacingMode] = useState('environment');
  const [muted, setMuted] = useState(false);
  const [flash, setFlash] = useState(null); // 'success' | 'error' | null
  const [lastScan, setLastScan] = useState(null);
  const [status, setStatus] = useState('idle'); // 'idle' | 'validating' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const inFlightRef = useRef(false);
  const flashTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // Full reset on every visible=false transition. Re-opening starts clean.
  useEffect(() => {
    if (visible) return;
    setFlash(null);
    setLastScan(null);
    setStatus('idle');
    setErrorMessage(null);
    setCameraError(null);
    setFacingMode('environment');
    inFlightRef.current = false;
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, [visible]);

  // Cleanup timers + mark unmounted so any in-flight await skips setState
  useEffect(
    () => () => {
      mountedRef.current = false;
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const scheduleAutoClose = useCallback((delayMs) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      // Only call onCancel if we're still mounted and visible — otherwise the
      // parent has already closed us via another path.
      if (mountedRef.current && visibleRef.current && onCancelRef.current) {
        onCancelRef.current();
      }
    }, delayMs);
  }, []);

  const handleDecode = useCallback(
    async (decoded) => {
      if (inFlightRef.current) return; // already handed off this open
      if (!decoded) return;
      inFlightRef.current = true;
      setLastScan(decoded);
      setStatus('validating');
      // Neutral acknowledge tone the instant a QR is read — gives the user
      // feedback that the camera saw something, before validation completes.
      playSuccessFeedback(mutedRef.current);

      let result;
      try {
        const handler = onScanRef.current;
        result = handler ? await Promise.resolve(handler(decoded)) : undefined;
      } catch (_err) {
        // Caller threw. Treat as failure so the user sees something instead
        // of being stuck on 'Validating…'.
        result = {
          ok: false,
          message: 'Scan could not be processed. Please try again.'
        };
      }

      // If the modal was closed (cancel, parent state change) or unmounted
      // while we were awaiting the caller's validation, do not change UI or
      // schedule close. The caller's state mutations (if any) inside onScan
      // still happened — that's the caller's responsibility, not ours.
      if (!mountedRef.current || !visibleRef.current) return;

      const ok = !result || result.ok !== false;
      if (ok) {
        setStatus('success');
        setErrorMessage(null);
        setFlash('success');
        // Distinct success cue (separate tone from the neutral acknowledge).
        playSuccessFeedback(mutedRef.current);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlash(null), 450);
        scheduleAutoClose(SUCCESS_HOLD_MS);
      } else {
        const msg =
          (result && result.message) || 'Scan rejected. Please try again.';
        setStatus('error');
        setErrorMessage(msg);
        setFlash('error');
        playErrorFeedback(mutedRef.current);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlash(null), 500);
        // Mirror the failure to the background screen with the same antd
        // message API the caller used to call directly. Same text, same
        // styling, same lifecycle — validated screens see identical UX.
        try {
          antdMessage.error(msg);
        } catch (_) {
          /* defensive: antd message should always work */
        }
        scheduleAutoClose(ERROR_HOLD_MS);
      }
    },
    [scheduleAutoClose]
  );

  // The shared QrCamera surfaces friendly errors that end with "Use manual
  // entry below." That line is misleading here because this modal is
  // camera-only — strip it so the user just sees the root cause.
  const handleCameraError = useCallback((msg) => {
    const cleaned = (msg || '')
      .replace(/\s*Use manual entry below\.?\s*$/i, '')
      .trim();
    setCameraError(cleaned || 'Camera unavailable. Please close and retry.');
  }, []);

  const switchCamera = () =>
    setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'));

  const toggleMute = () => setMuted((m) => !m);

  // While we're auto-closing or validating, the Cancel button still works
  // (user-initiated close should always be honoured immediately).
  const handleManualCancel = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (onCancelRef.current) onCancelRef.current();
  }, []);

  const flashClass =
    flash === 'success'
      ? styles.flash_success
      : flash === 'error'
      ? styles.flash_error
      : flash === 'duplicate'
      ? styles.flash_duplicate
      : null;

  return (
    <CustomModal
      title={title}
      visible={visible}
      onCancel={handleManualCancel}
      customFooter={false}
      footer={null}
      width={560}
      destroyOnClose
    >
      <div className={styles.scanner_shell}>
        <QrCamera
          active={visible}
          facingMode={facingMode}
          onDecode={handleDecode}
          onCameraError={handleCameraError}
        />

        {/* Top overlay: switch camera + mute */}
        <div className={styles.overlay_top}>
          <button
            type="button"
            className={styles.overlay_icon_btn}
            onClick={switchCamera}
            disabled={!!cameraError || status !== 'idle'}
            aria-label="Switch camera"
            title="Switch camera"
          >
            <SwapOutlined />
          </button>
          <span aria-hidden="true" />
          <button
            type="button"
            className={styles.overlay_icon_btn}
            onClick={toggleMute}
            aria-label={muted ? 'Unmute scan feedback' : 'Mute scan feedback'}
            title={muted ? 'Sound off' : 'Sound on'}
          >
            {muted ? <SoundOutlined /> : <SoundFilled />}
          </button>
        </div>

        {/* Flash overlay */}
        {flash && flashClass && (
          <div
            key={`flash-${flash}-${lastScan || ''}-${Date.now()}`}
            className={`${styles.flash} ${flashClass}`}
          />
        )}

        {/* Validating overlay — non-blocking, just a hint that we're awaiting
            the caller. The camera feed still shows underneath. */}
        {status === 'validating' && (
          <div className={styles.scanner_loading}>
            <Spin />
            <span>Validating…</span>
          </div>
        )}

        {/* Bottom toast over the camera for success/error */}
        {status === 'success' && lastScan && (
          <div className={`${styles.toast} ${styles.toast_success}`}>
            <CheckCircleFilled />
            <span>{lastScan} confirmed</span>
          </div>
        )}
        {status === 'error' && errorMessage && (
          <div className={`${styles.toast} ${styles.toast_error}`}>
            <CloseCircleFilled />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>

      {/* Status / hint line under camera */}
      <div className={styles.section_status}>
        {cameraError ? (
          <span style={{ color: '#ff4d4f' }}>{cameraError}</span>
        ) : status === 'error' && errorMessage ? (
          <span style={{ color: '#ff4d4f' }}>{errorMessage}</span>
        ) : lastScan ? (
          <>
            <span className={styles.section_status_label}>
              {status === 'success' ? 'Confirmed:' : 'Scanned:'}
            </span>
            <span style={{ fontFamily: 'monospace' }}>{lastScan}</span>
          </>
        ) : (
          <span style={{ color: '#8c8c8c' }}>
            {hint || 'Point the camera at a QR code to begin.'}
          </span>
        )}
      </div>

      <div className={styles.footer}>
        <Button className={styles.footer_button} onClick={handleManualCancel}>
          Cancel
        </Button>
      </div>
    </CustomModal>
  );
};

export default QrScanSingleShotModal;
